import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Typography, FormControlLabel, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { EmptySectionMessage, FullPageLoader } from '@tassomutuo-backoffice/ui';
import { getLeadsDropped } from '../../services/api/lead/lead';
import LeadCardList from './components/LeadCardList';
import LeadFilterSection from './components/LeadFilterSection';
import { LeadProductChip } from '../../components';

const LeadsDroppedPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { isPending, data, error } = useQuery({
    queryKey: ['leadsDropped'],
    queryFn: getLeadsDropped,
  });

  const leadsUnderManagement = data?.results || [];

  // Leggi i filtri dall'URL
  const searchFilter = searchParams.get('search') || '';
  const showOnlyMutuoPerso = searchParams.get('mutuoPerso') === 'true';

  // Calcola le lead filtrate
  const filteredLeads = useMemo(() => {
    if (!leadsUnderManagement.length) return [];

    let filtered = [...leadsUnderManagement];

    // Applica filtro ricerca testuale
    if (searchFilter) {
      const searchLower = searchFilter.toLowerCase();
      filtered = filtered.filter((lead) =>
        lead.properties.dealname.toLowerCase().includes(searchLower)
      );
    }

    // Applica filtro Mutuo Perso
    if (showOnlyMutuoPerso) {
      filtered = filtered.filter(
        (lead) => lead.properties?.mutuo_perso === 'true'
      );
    }

    return filtered;
  }, [leadsUnderManagement, searchFilter, showOnlyMutuoPerso]);

  const handleMutuoPersoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (event.target.checked) {
        newParams.set('mutuoPerso', 'true');
      } else {
        newParams.delete('mutuoPerso');
      }
      return newParams;
    });
  };

  return (
    <>
      <FullPageLoader show={isPending} />
      <Box>
        <Box px={4} pt={4} pb={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <CloseIcon fontSize="large" />
          <Typography variant="h4" fontWeight="bold">
            Lead Dropped
          </Typography>
          <LeadProductChip product="mutui" />
        </Box>

        <Box 
          display="flex" 
          flexDirection="row"
          alignItems={"center"} 
          justifyContent={"end"}
        >

          {/* Mutuo Perso Checkbox Filter */}
          <Box display="flex" alignItems="center">
            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Checkbox
                  checked={showOnlyMutuoPerso}
                  onChange={handleMutuoPersoChange}
                  color="primary"
                />
              }
              label={
                <Typography fontWeight="medium">
                  Mutuo Perso
                </Typography>
              }
            />
          </Box>

          <LeadFilterSection
            showFilters={false}
            leads={leadsUnderManagement}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        </Box>

        {!isPending && error && (
          <Alert severity="error">
            {error.message ? `Error: ${error.message}` : 'An Error occurred'}
          </Alert>
        )}
        
        {filteredLeads && filteredLeads?.map((lead) => <LeadCardList key={lead.id} lead={lead} />)}

        {filteredLeads && filteredLeads.length === 0 && (
          <EmptySectionMessage title="Non ci sono lead da mostrare" />
        )}
      </Box>
    </>
  );
};

export default LeadsDroppedPage;
